import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { FiZap, FiCpu, FiShield, FiBarChart2, FiLayers, FiHeadphones } from "react-icons/fi";
import { useTheme } from "../context/ThemeContext";

const EASE = [0.22, 1, 0.36, 1];

export default function Features() {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const TOKENS = useMemo(
    () =>
      isDark
        ? {
            text: "#FFFFFF",
            muted: "rgba(255,255,255,.78)",
            panel: "rgba(12,18,30,.82)",
            ring: "rgba(255,255,255,.10)",
            grid: "rgba(255,255,255,.08)",
            shadow: "0 36px 80px rgba(0,0,0,.45)",
            bgStart: "#0B1220",
            bgMid: "#0B1325",
            bgEnd: "#000",
          }
        : {
            text: "#0B1220",
            muted: "rgba(17,24,39,.72)",
            panel: "#FFFFFF",
            ring: "rgba(2,6,23,.10)",
            grid: "rgba(0,0,0,.06)",
            shadow: "0 28px 64px rgba(2,6,23,.14)",
            bgStart: "#F6FAFF",
            bgMid: "#EEF3F9",
            bgEnd: "#FFFFFF",
          },
    [isDark]
  );

  const features = [
    {
      icon: FiZap,
      title: "Speed & DX",
      desc: "Next.js + edge caching + image/CDN optimization for sub-second UX.",
      bullets: ["SSR/SSG mix", "Code-split routes", "Asset prefetch"],
    },
    {
      icon: FiCpu,
      title: "AI-First",
      desc: "Agents, chatbots, smart forms — measurable automation.",
      bullets: ["RAG/search", "Lead scoring", "Workflow bots"],
    },
    {
      icon: FiShield,
      title: "Security",
      desc: "Auth, RBAC, audit trails & hardened hosting pipelines.",
      bullets: ["JWT/SSO", "Field level rules", "Backups"],
    },
    {
      icon: FiBarChart2,
      title: "Analytics",
      desc: "Funnel, cohorts, heatmaps — decisions backed by data.",
      bullets: ["Event schema", "BI dashboards", "Attribution"],
    },
    {
      icon: FiLayers,
      title: "Design System",
      desc: "Pixel-perfect components, micro-interactions & themes.",
      bullets: ["Tokenized UI", "Light/Dark", "A11y"],
    },
    {
      icon: FiHeadphones,
      title: "Support",
      desc: "SLA, alerts, observability, fast iterative shipping.",
      bullets: ["On-call", "Telemetry", "Roadmaps"],
    },
  ];

  return (
    <section
      className="relative min-h-[70vh] overflow-hidden font-inter"
      style={{ color: TOKENS.text }}
    >
      {/* background */}
      <div className="absolute inset-0 -z-10">
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(135deg, ${TOKENS.bgStart}, ${TOKENS.bgMid}, ${TOKENS.bgEnd})`,
          }}
        />
        <div
          className="absolute -top-24 -left-24 h-[34rem] w-[34rem] rounded-full blur-3xl"
          style={{ background: "radial-gradient(closest-side, rgba(16,185,129,.25), transparent)" }}
        />
        <div
          className="absolute bottom-[-8rem] right-[-8rem] h-[30rem] w-[30rem] rounded-full blur-3xl"
          style={{ background: "radial-gradient(closest-side, rgba(6,182,212,.22), transparent)" }}
        />
        <div
          className="absolute inset-0 opacity-[0.06] [mask-image:linear-gradient(to_bottom,black,transparent_95%)]"
          style={{
            backgroundImage: `linear-gradient(${TOKENS.grid} 1px, transparent 1px), linear-gradient(90deg, ${TOKENS.grid} 1px, transparent 1px)`,
            backgroundSize: "44px 44px",
          }}
        />
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
        <div className="text-center">
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight">
            Powerful <span className="bg-clip-text text-transparent" style={{ backgroundImage: "linear-gradient(90deg, var(--brand,#1FA550), var(--accent,#06B6D4))" }}>features</span>
          </h1>
          <p className="mt-3 text-lg" style={{ color: TOKENS.muted }}>
            Opinionated defaults. Business outcomes. No fluff.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <motion.article
              key={f.title}
              initial={{ opacity: 0, y: 18, scale: 0.98 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.5, ease: EASE, delay: i * 0.04 }}
              className="relative rounded-2xl p-[1.2px]"
              style={{
                background:
                  "conic-gradient(from 90deg at 50% 50%, rgba(16,185,129,.35), rgba(6,182,212,.35), transparent 60%)",
                boxShadow: TOKENS.shadow,
              }}
            >
              <div
                className="rounded-2xl h-full p-6 ring-1 backdrop-blur"
                style={{ background: TOKENS.panel, borderColor: TOKENS.ring }}
              >
                <div className="flex items-center gap-3">
                  <span className="grid h-11 w-11 place-items-center rounded-xl text-black"
                        style={{ background: "linear-gradient(135deg, var(--brand,#1FA550), var(--accent,#06B6D4))" }}>
                    <f.icon />
                  </span>
                  <h3 className="text-lg font-semibold">{f.title}</h3>
                </div>
                <p className="mt-3 text-sm" style={{ color: TOKENS.muted }}>{f.desc}</p>
                <ul className="mt-4 grid gap-2 text-sm">
                  {f.bullets.map((b) => (
                    <li key={b} className="flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full"
                            style={{ background: "linear-gradient(135deg, var(--brand,#1FA550), var(--accent,#06B6D4))" }} />
                      <span style={{ color: TOKENS.muted }}>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.article>
          ))}
        </div>
      </div>

      <style>{`.font-inter{font-family:Inter,ui-sans-serif,system-ui,-apple-system,'Segoe UI',Roboto,Helvetica,Arial}`}</style>
    </section>
  );
}
